<script setup>
import {onBeforeMount, reactive, ref, watch, defineProps, inject} from 'vue';
import Status from '../constants/OnboardingStatus';
import {TypeOfPlanList, TypeOfPlanConst} from '../constants/TypeOfPlan';
import {
  TypeOfActivityList,
  TypeOfActivityConst,
} from '../constants/TypeOfActivity';
import {APIService} from '@/core/util/services/api.service';
import TrainingDialog from '../../core/components/dialogs/TrainingDialog.vue';
import InformationDialog from '../../core/components/dialogs/InformationDialog.vue';
import TaskDialog from '../../core/components/dialogs/TaskDialog.vue';
import {navigate} from '@/core/util/helper/navigation';
import MultiChips from '@ohrm/oxd/core/components/Input/MultiSelect/MultiSelectChips.vue';
import fileInput from '../../orangehrmPimPlugin/components/FileInput.vue';
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import PlanOfActivities from '../components/onboarding/PlanOfActivities.vue';

const $toast = inject('$toast');

const props = defineProps({
  id: {
    type: Number,
    default() {
      return null;
    },
  },
  departments: {
    type: Array,
  },
  isShowPage: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const http = new APIService(window.appGlobal.baseUrl, 'api/v2/pim/employees');

const isLoading = ref(false);

const informationsList = ref([]);
const trainingsList = ref([]);
const tasksList = ref([]);

// Dialogs
const trainingDialog = ref(null);
const informationDialog = ref(null);
const taskDialog = ref(null);
const reRenderPlanManagerInput = ref(0);

// Form
const form = reactive({
  title: '',
  status: null,
  description: '',
  dueDate: '',
  responsible: null,

  departments: [],
  subUnits: [],
  assignedUsers: [],

  files: [],

  selectedTypeOfPlan: null,
  selectedTypeOfActivities: [],

  trainingsPlans: [],
  informations: [],
  tasks: [],
  selectedTrainings: [],
  selectedTraining: null,
  selectedInformations: [],
  selectedInformation: null,
  selectedTasks: [],
  selectedTask: null,
  stages: [],
});

const subUnits = ref([]);
const employees = ref([]);
const filterEmployees = ref([]);
const canSubmit = ref(true);

const rules = ref({
  title: [required, shouldNotExceedCharLength(255)],
  status: [required],
  typeOfPlan: [required],
  typeOfActivities: [required],
  information: [required],
  training: [required],
  task: [required],
  planManager: [required],
});

const deletedStageActivities = ref([]);
const deletedStages = ref([]);

const getEmployees = async () => {
  await http
    .request({
      method: 'GET',
      url: `/api/training/get-all-employees`,
    })
    .then(response => {
      employees.value = response.data.data.map(employee => {
        return {
          id: employee.empNumber,
          label: `${employee.firstName} ${employee.middleName} ${employee.lastName}`,
          department: employee.department.id,
          subunit: employee.subUnit.id,
        };
      });

      filterEmployees.value = employees.value;
    });
};

const onSave = () => {
  isLoading.value = true;

  const formData = new FormData();
  if (props.id != null) formData.append('id', props.id);
  formData.append('title', form.title);
  formData.append('status_id', form.status.id);
  formData.append('description', form.description);

  formData.append('due_date', form.dueDate ?? '');
  if (form.responsible?.id)
    formData.append('responsible', form.responsible?.id);

  form.departments.forEach(department => {
    formData.append('departments[]', department.id);
  });

  form.subUnits.forEach(subunit => {
    formData.append('subUnits[]', subunit.id);
  });

  form.assignedUsers.forEach(assign => {
    formData.append('assigned_users[]', assign.id);
  });

  form.files.forEach(file => {
    let value = file.value;
    if (!value.size) {
      value = JSON.stringify(file.value);
    }
    formData.append('files[]', value);
  });

  formData.append('typeOfPlanId', form.selectedTypeOfPlan?.id);

  form.selectedTypeOfActivities.forEach(activity => {
    formData.append('typeOfActivities[]', activity?.id);
  });

  switch (form.selectedTypeOfPlan?.id) {
    case TypeOfPlanConst.SINGLE_ACTIVITY:
      if (
        form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.INFORMATION
      ) {
        formData.append('informations[]', form.selectedInformation?.id);
      }
      if (
        form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.TRAINING
      ) {
        formData.append('trainings[]', form.selectedTraining?.id);
      }
      if (form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.TASK) {
        formData.append('tasks[]', form.selectedTask?.id);
      }
      break;
    case TypeOfPlanConst.MULTIPLE_ACTIVITIES:
      form.selectedInformations.forEach(information => {
        formData.append('informations[]', information?.id);
      });
      form.selectedTrainings.forEach(training => {
        formData.append('trainings[]', training?.id);
      });
      form.selectedTasks.forEach(task => {
        formData.append('tasks[]', task?.id);
      });
      break;
    case TypeOfPlanConst.PLAN_OF_ACTIVITIES:
      formData.append('stages', JSON.stringify(form.stages));
      if (props.id != null) {
        if (deletedStageActivities.value.length != 0) {
          formData.append(
            'deletedStageActivities',
            JSON.stringify(deletedStageActivities.value),
          );
        }
        if (deletedStages.value.length != 0) {
          formData.append('deletedStages', JSON.stringify(deletedStages.value));
        }
      }
      break;
  }

  let url = '/api/onboarding';
  if (props.id != null) url = `${url}?_method=PUT`;

  http
    .request({
      url: url,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data: formData,
    })
    .then(() => {
      props.id != null ? $toast.updateSuccess() : $toast.saveSuccess();
      navigate('/training/viewOnboarding');
    })
    .catch(() => {
      $toast.unexpectedError();
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onCancel = () => {
  navigate('/training/viewOnboarding');
};

const getTrainings = async () => {
  await http
    .request({
      method: 'GET',
      url: '/api/trainings-list',
    })
    .then(response => {
      trainingsList.value = response.data;
    });
};

const getInformations = async () => {
  await http
    .request({
      method: 'GET',
      url: '/api/information-list',
    })
    .then(response => {
      informationsList.value = response.data;
    });
};

const getTasks = async () => {
  await http
    .request({
      method: 'GET',
      url: '/api/task-list',
    })
    .then(response => {
      tasksList.value = response.data ?? [];
    });
};

const showTrainingModal = (stageIndex = null, stageActivityIndex = null) => {
  trainingDialog.value.showDialog().then(response => {
    trainingsList.value.push({id: response.id, label: response.name});

    switch (form.selectedTypeOfPlan.id) {
      case TypeOfPlanConst.SINGLE_ACTIVITY:
        form.selectedTraining = {
          id: response.id,
          label: response.name,
          _selected: false,
        };
        break;
      case TypeOfPlanConst.PLAN_OF_ACTIVITIES:
        form.stages[stageIndex].infos[stageActivityIndex].activity = {
          id: response.id,
          label: response.name,
          _selected: false,
        };
        break;
      default:
        form.selectedTrainings.push({
          id: response.id,
          label: response.name,
          _selected: false,
        });
        break;
    }
  });
};

const showInformationModal = (stageIndex = null, stageActivityIndex = null) => {
  informationDialog.value.showDialog().then(response => {
    informationsList.value.push({id: response.id, label: response.name});
    switch (form.selectedTypeOfPlan.id) {
      case TypeOfPlanConst.SINGLE_ACTIVITY:
        form.selectedInformation = {
          id: response.id,
          label: response.name,
          _selected: false,
        };
        break;
      case TypeOfPlanConst.PLAN_OF_ACTIVITIES:
        form.stages[stageIndex].infos[stageActivityIndex].activity = {
          id: response.id,
          label: response.name,
          _selected: false,
        };
        break;
      default:
        form.selectedInformations.push({
          id: response.id,
          label: response.name,
          _selected: false,
        });
        break;
    }
  });
};

const showTaskModal = (stageIndex = null, stageActivityIndex = null) => {
  taskDialog.value.showDialog().then(response => {
    tasksList.value.push({id: response.id, label: response.title});
    switch (form.selectedTypeOfPlan.id) {
      case TypeOfPlanConst.SINGLE_ACTIVITY:
        form.selectedTask = {
          id: response.id,
          label: response.title,
          _selected: false,
        };
        break;
      case TypeOfPlanConst.PLAN_OF_ACTIVITIES:
        form.stages[stageIndex].infos[stageActivityIndex].activity = {
          id: response.id,
          label: response.title,
          _selected: false,
        };
        break;
      default:
        form.selectedTasks.push({
          id: response.id,
          label: response.title,
          _selected: false,
        });
        break;
    }
  });
};

const clearMultipleActivitiesOptions = () => {
  form.selectedInformations = [];
  form.selectedTrainings = [];
  form.selectedTasks = [];
  form.stages = [];
};

const clearSingleActivityOptions = () => {
  form.selectedInformation = null;
  form.selectedTraining = null;
  form.selectedTask = null;
};

const changeActivitiesValuesByTypeOfPlan = newTypeOfPlanId => {
  switch (newTypeOfPlanId) {
    case TypeOfPlanConst.SINGLE_ACTIVITY:
      if (
        form.selectedInformations.length > 0 &&
        form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.INFORMATION
      ) {
        form.selectedInformation = form.selectedInformations[0];
      }

      if (
        form.selectedTrainings.length > 0 &&
        form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.TRAINING
      ) {
        form.selectedTraining = form.selectedTrainings[0];
      }
      if (
        form.selectedTasks.length > 0 &&
        form.selectedTypeOfActivities[0]?.id == TypeOfActivityConst.TASK
      ) {
        form.selectedTask = form.selectedTasks[0];
      }

      clearMultipleActivitiesOptions();
      break;
    case TypeOfPlanConst.MULTIPLE_ACTIVITIES:
      if (
        form.selectedInformation != null &&
        Object.keys(form.selectedInformation).length != 0
      ) {
        form.selectedInformations.push(form.selectedInformation);
      }
      if (
        form.selectedTraining != null &&
        Object.keys(form.selectedTraining).length != 0
      ) {
        form.selectedTrainings.push(form.selectedTraining);
      }
      if (
        form.selectedTask != null &&
        Object.keys(form.selectedTask).length != 0
      ) {
        form.selectedTasks.push(form.selectedTask);
      }
      clearSingleActivityOptions();
      break;
  }
};

const addAttachment = event => {
  const id = crypto.randomUUID();
  const file = event.target.files[0];
  const fileName = file.name;
  form.files.push({
    id: id,
    label: fileName,
    version: '',
    value: file,
  });
};

const removeAttachment = id => {
  const index = form.files.findIndex(f => f.id === id);

  form.files.splice(index, 1);
};

const getDepartmentSubUnits = async departments => {
  const departmentIds = departments.map(department => department.id);
  await http
    .request({
      method: 'GET',
      url: '/api/training/departments/sub-units',
      params: {departmentIds},
    })
    .then(response => {
      subUnits.value = response.data;
      subUnits.value.unshift({
        id: 0,
        label: 'All',
      });
    });
};

const getOnboardingData = async () => {
  isLoading.value = true;
  await http
    .request({
      url: `api/onboarding/${props.id}`,
    })
    .then(response => {
      form.title = response.data.title;
      form.status = response.data.status;
      form.description = response.data.description;
      form.dueDate = response.data.dueDate;
      form.responsible = response.data.responsible;
      form.departments = response.data.departments;
      form.subUnits = response.data.subunits;
      form.assignedUsers = response.data.assignedUsers;
      form.selectedTypeOfPlan = response.data.selectedTypeOfPlan;
      form.selectedTypeOfActivities = response.data.selectedTypeOfActivities;
      if (
        response.data.selectedTypeOfPlan?.id == TypeOfPlanConst.SINGLE_ACTIVITY
      ) {
        if (
          response.data.selectedTypeOfActivitiesIds.includes(
            TypeOfActivityConst.TASK,
          )
        ) {
          form.selectedTask = response.data.activities[0]?.activity;
        }
        if (
          response.data.selectedTypeOfActivitiesIds.includes(
            TypeOfActivityConst.TRAINING,
          )
        ) {
          form.selectedTraining = response.data.activities[0]?.activity;
        }
        if (
          response.data.selectedTypeOfActivitiesIds.includes(
            TypeOfActivityConst.INFORMATION,
          )
        ) {
          form.selectedInformation = response.data.activities[0]?.activity;
        }
      }

      if (
        response.data.selectedTypeOfPlan?.id ==
        TypeOfPlanConst.MULTIPLE_ACTIVITIES
      ) {
        const allActivities = response.data.activities.reduce(
          (result, activity) => {
            switch (activity?.typeOfActivity?.id) {
              case TypeOfActivityConst.TASK:
                result.tasks.push(activity?.activity);
                break;
              case TypeOfActivityConst.TRAINING:
                result.trainings.push(activity?.activity);
                break;
              case TypeOfActivityConst.INFORMATION:
                result.informations.push(activity?.activity);
                break;
            }

            return result;
          },
          {trainings: [], informations: [], tasks: []},
        );

        form.selectedTasks = allActivities.tasks;
        form.selectedTrainings = allActivities.trainings;
        form.selectedInformations = allActivities.informations;
      }

      if (
        response.data.selectedTypeOfPlan?.id ==
        TypeOfPlanConst.PLAN_OF_ACTIVITIES
      ) {
        form.stages = response.data.activities;
      }

      response.data.files.forEach(file => {
        form.files.push({
          id: form.files.length + 1,
          label: file?.value?.split('/')[file?.value?.split('/').length - 1],
          version: '',
          value: file,
          uploaded: true,
        });
      });
    })
    .catch(error => {
      $toast.unexpectedError();
    })
    .finally(() => {
      isLoading.value = false;
    });
};
watch(
  () => form.selectedTypeOfPlan,
  (newType, oldType) => {
    if (
      newType?.id == TypeOfPlanConst.SINGLE_ACTIVITY &&
      form.selectedTypeOfActivities.length > 0
    ) {
      form.selectedTypeOfActivities.splice(1);
    } else if (
      (newType?.id == TypeOfPlanConst.PLAN_OF_ACTIVITIES ||
        oldType?.id == TypeOfPlanConst.PLAN_OF_ACTIVITIES) &&
      oldType?.id != undefined
    ) {
      form.selectedTypeOfActivities = [];
      clearSingleActivityOptions();
      clearMultipleActivitiesOptions();
      form.tasks.value = [];
    }
    reRenderPlanManagerInput.value += 1;

    changeActivitiesValuesByTypeOfPlan(newType?.id);
  },
);

watch(
  () => form.selectedTypeOfActivities,
  newActivity => {
    if (form.selectedTypeOfPlan?.id == TypeOfPlanConst.SINGLE_ACTIVITY) {
      // TO CLEAR ONLY THE VALUES OF THE OTHER OPTIONS WHEN TYPE OF PLAN CHANGE
      switch (newActivity.id) {
        case TypeOfActivityConst.TASK:
          form.selectedInformation = null;
          form.selectedTraining = null;
          break;
        case TypeOfActivityConst.INFORMATION:
          form.selectedTraining = null;
          form.task = [];
          break;
        case TypeOfActivityConst.TRAINING:
          form.selectedInformation = null;
          form.task = [];
          break;
      }
    }
  },
  {deep: true},
);

watch(
  () => form.departments,
  departments => {
    if (departments.find(department => department.id === 0)) {
      departments = props.departments.filter(department => department.id != 0);
    }

    const departmentIds = departments.map(department => department.id);

    filterEmployees.value = employees.value.filter(employee => {
      return !departmentIds.includes(employee.department);
    });

    subUnits.value = [];
    getDepartmentSubUnits(departments);

    // caso não haja departments selecionados, remove todas as seleções de subUnits
    if (departments.length == 0) form.subUnits = [];
  },
);

watch(
  () => form.subUnits,
  subUnits => {
    const departmentIds = form.departments.map(department => department.id);
    const subUnitIds = subUnits.map(subUnit => subUnit.id);
    filterEmployees.value = employees.value.filter(employee => {
      return (
        !departmentIds.includes(employee.department) &&
        !subUnitIds.includes(employee.subunit)
      );
    });
  },
);

const dueDateAssociationsRules = ref([]);

const validateDueDateAssociations = async () => {
  const url = '/api/onboarding/validate-due-date-associations';

  const deps = form.departments.map(d => d.id);
  const subu = form.subUnits.map(d => d.id);
  const assUsers = form.assignedUsers.map(d => d.id);
  const train = [];
  const infor = [];

  if (form.selectedTypeOfPlan) {
    switch (form.selectedTypeOfPlan.id) {
      case TypeOfPlanConst.SINGLE_ACTIVITY:
        if (form.selectedTypeOfActivities.length > 0) {
          if (
            form.selectedTypeOfActivities[0].id == TypeOfActivityConst.TRAINING
          ) {
            if (
              form.selectedTraining &&
              !train.includes(form.selectedTraining.id)
            ) {
              train.push(form.selectedTraining.id);
            }
          }

          if (
            form.selectedTypeOfActivities[0].id ==
            TypeOfActivityConst.INFORMATION
          ) {
            if (
              form.selectedInformation &&
              !infor.includes(form.selectedInformation.id)
            ) {
              infor.push(form.selectedInformation.id);
            }
          }
        }
        break;
      case TypeOfPlanConst.MULTIPLE_ACTIVITIES:
        form.selectedTrainings.forEach(st => {
          if (!train.includes(st.id)) {
            train.push(st.id);
          }
        });

        form.selectedInformations.forEach(si => {
          if (!infor.includes(si.id)) {
            infor.push(si.id);
          }
        });
        break;
      case TypeOfPlanConst.PLAN_OF_ACTIVITIES:
        form.stages.forEach(stage => {
          stage.infos.forEach(info => {
            if (info.typeOfActivity?.id == TypeOfActivityConst.TRAINING) {
              if (!train.includes(info.activity.id)) {
                train.push(info.activity.id);
              }
            }

            if (info.typeOfActivity?.id == TypeOfActivityConst.INFORMATION) {
              if (!infor.includes(info.activity.id)) {
                infor.push(info.activity.id);
              }
            }
          });
        });
        break;
      default:
        break;
    }
  }

  http
    .request({
      url: url,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data: {
        departments: JSON.stringify(deps),
        subunits: JSON.stringify(subu),
        assignedUsers: JSON.stringify(assUsers),
        trainings: JSON.stringify(train),
        informations: JSON.stringify(infor),
      },
    })
    .then(response => {
      dueDateAssociationsRules.value = response.data.errors;
      canSubmit.value = response.data.canSubmit;
    });
};

watch(
  () => [
    form.departments,
    form.subUnits,
    form.assignedUsers,
    form.selectedTask,
    form.selectedTasks,
    form.selectedInformation,
    form.selectedInformations,
    form.selectedTraining,
    form.selectedTrainings,
    form.stages,
  ],
  () => {
    dueDateAssociationsRules.value = [];
    if (
      form.departments.length != 0 ||
      form.subUnits.length != 0 ||
      form.assignedUsers.length != 0 ||
      form.assignedUsers.length == 0
    ) {
      validateDueDateAssociations();
    }
  },
  {deep: true},
);

onBeforeMount(() => {
  if (props.id) getOnboardingData();
  getEmployees();
  getTrainings();
  getInformations();
  getTasks();
});
</script>
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        <template v-if="props.id && isShowPage == false">
          Edit Onboarding
        </template>
        <template v-else>
          Add Onboarding
        </template>
      </oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="form.title"
                label="Title"
                placeholder="Title"
                :rules="rules.title"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.status"
                label="Status"
                value="Select"
                type="select"
                :options="Status"
                :rules="rules.status"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="form.description"
            type="textarea"
            label="Description"
            placeholder="Description"
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input v-model="form.dueDate" label="Due Date" />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                :key="reRenderPlanManagerInput"
                v-model="form.responsible"
                label="Plan Manager"
                value="Select"
                type="select"
                :rules="
                  form.selectedTypeOfPlan?.id !=
                  TypeOfPlanConst.PLAN_OF_ACTIVITIES
                    ? rules.planManager
                    : []
                "
                :required="
                  form.selectedTypeOfPlan?.id !=
                    TypeOfPlanConst.PLAN_OF_ACTIVITIES
                "
                :options="employees"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <!-- ASSIGN SECTION  -->
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.departments"
                label="Department(s)"
                value="Select"
                type="multiselect"
                :options="props.departments"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.subUnits"
                label="Sub Unit(s)"
                value="Select"
                type="multiselect"
                :options="subUnits"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.assignedUsers"
                label="Assign to"
                value="Select"
                type="multiselect"
                :options="filterEmployees"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <!-- END: ASSIGN SECTION  -->
        <!-- FILE UPLOAD SECTION -->
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="oxd-input-group oxd-input-field-bottom-space">
                <file-input
                  label="Files"
                  :required="false"
                  @change="addAttachment"
                />
              </div>
            </oxd-grid-item>
            <oxd-grid-item class="flex items-center --span-column-2">
              <MultiChips
                :selected="form.files"
                @chipRemoved="file => removeAttachment(file.id)"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <!-- END: FILE UPLOAD SECTION -->
        <!-- ACTIVITIES SECTION -->
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.selectedTypeOfPlan"
                label="Type of Plan"
                value="Select"
                type="select"
                :rules="rules.typeOfPlan"
                :options="TypeOfPlanList"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-if="
                  form.selectedTypeOfPlan?.id == TypeOfPlanConst.SINGLE_ACTIVITY
                "
                v-model="form.selectedTypeOfActivities[0]"
                label="Type of Activity"
                type="select"
                :options="TypeOfActivityList"
                :disabled="form.selectedTypeOfPlan == null"
                :rules="rules.typeOfActivities"
                required
              />
              <oxd-input-field
                v-if="
                  form.selectedTypeOfPlan?.id ==
                    TypeOfPlanConst.MULTIPLE_ACTIVITIES
                "
                v-model="form.selectedTypeOfActivities"
                label="Type of Activities"
                value="Select"
                type="multiselect"
                :options="TypeOfActivityList"
                :disabled="form.selectedTypeOfPlan == null"
                :rules="rules.typeOfActivities"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row
          v-if="form.selectedTypeOfPlan != null"
          class="oxd-input-field-bottom-space"
        >
          <oxd-text
            v-if="
              form.selectedTypeOfPlan?.id == TypeOfPlanConst.SINGLE_ACTIVITY
            "
            tag="h6"
            class="orangehrm-main-title"
          >
            Single Activity
          </oxd-text>
          <oxd-text
            v-if="
              form.selectedTypeOfPlan?.id == TypeOfPlanConst.MULTIPLE_ACTIVITIES
            "
            tag="h6"
            class="orangehrm-main-title"
          >
            Multiple Activities
          </oxd-text>
          <oxd-text
            v-if="
              form.selectedTypeOfPlan?.id == TypeOfPlanConst.PLAN_OF_ACTIVITIES
            "
            tag="h6"
            class="orangehrm-main-title"
          >
            Plan of Activities
          </oxd-text>
          <oxd-divider />
          <oxd-text
            v-if="
              form.selectedTypeOfActivities.length == 0 &&
                form.selectedTypeOfPlan?.id !=
                  TypeOfPlanConst.PLAN_OF_ACTIVITIES
            "
            tag="p"
            class="orangehrm-sub-title"
            >Select one type of activity</oxd-text
          >
          <!-- PLANS WITH ACTIVITY TYPE -->
          <template
            v-for="(activity, index) in form.selectedTypeOfActivities"
            :key="index"
          >
            <template v-if="activity.id == TypeOfActivityConst.TRAINING">
              <oxd-text tag="h6" class="orangehrm-sub-title">
                Training
              </oxd-text>
              <oxd-divider />
              <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-if="
                      form.selectedTypeOfPlan?.id ==
                        TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    v-model="form.selectedTraining"
                    value="Select"
                    type="select"
                    :options="trainingsList"
                    :rules="rules.training"
                    required
                  />
                  <oxd-input-field
                    v-else
                    v-model="form.selectedTrainings"
                    value="Select"
                    type="multiselect"
                    :options="trainingsList"
                    :rules="rules.training"
                    required
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <oxd-button
                    :disabled="
                      form.selectedTraining != null &&
                        form.selectedTypeOfPlan?.id ==
                          TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    style="margin-top: 10px; padding: 8px"
                    label="New"
                    icon-name="plus"
                    display-type="text"
                    @click="showTrainingModal"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </template>
            <template v-if="activity.id == TypeOfActivityConst.INFORMATION">
              <oxd-text tag="h6" class="orangehrm-sub-title">
                Information
              </oxd-text>
              <oxd-divider />
              <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-if="
                      form.selectedTypeOfPlan?.id ==
                        TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    v-model="form.selectedInformation"
                    value="Select"
                    type="select"
                    :options="informationsList"
                    :rules="rules.information"
                    required
                  />
                  <oxd-input-field
                    v-else
                    v-model="form.selectedInformations"
                    value="Select"
                    type="multiselect"
                    :options="informationsList"
                    :rules="rules.information"
                    required
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <oxd-button
                    :disabled="
                      form.selectedInformation != null &&
                        form.selectedTypeOfPlan?.id ==
                          TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    style="margin-top: 10px; padding: 8px"
                    label="New"
                    icon-name="plus"
                    display-type="text"
                    @click="showInformationModal"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </template>
            <template v-if="activity.id == TypeOfActivityConst.TASK">
              <oxd-text tag="h6" class="orangehrm-sub-title">
                Task
              </oxd-text>
              <oxd-divider />
              <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-if="
                      form.selectedTypeOfPlan?.id ==
                        TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    v-model="form.selectedTask"
                    value="Select"
                    type="select"
                    :options="tasksList"
                    :rules="rules.task"
                    required
                  />
                  <oxd-input-field
                    v-else
                    v-model="form.selectedTasks"
                    value="Select"
                    type="multiselect"
                    :options="tasksList"
                    :rules="rules.task"
                    required
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <oxd-button
                    :disabled="
                      form.selectedTask != null &&
                        form.selectedTypeOfPlan?.id ==
                          TypeOfPlanConst.SINGLE_ACTIVITY
                    "
                    style="margin-top: 10px; padding: 8px"
                    label="New"
                    icon-name="plus"
                    display-type="text"
                    @click="showTaskModal"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </template>
          </template>
          <!-- END: PLANS WITH ACTIVITY TYPE -->
          <!-- PLAN OF ACTIVITIES -->
          <PlanOfActivities
            v-if="
              form.selectedTypeOfPlan?.id == TypeOfPlanConst.PLAN_OF_ACTIVITIES
            "
            :stages="form.stages"
            :informations-list="informationsList"
            :trainings-list="trainingsList"
            :tasks-list="tasksList"
            :responsibles="employees"
            :deleted-stages="deletedStages"
            :deleted-stage-activities="deletedStageActivities"
            :is-edit="props.id != null"
            @showInformationModal="showInformationModal"
            @showTrainingModal="showTrainingModal"
            @showTaskModal="showTaskModal"
          />
          <!-- END: PLAN OF ACTIVITIES -->
        </oxd-form-row>
        <!-- END: ACTIVITIES SECTION -->

        <oxd-form-row v-if="dueDateAssociationsRules.length > 0">
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="error-message">
                <span class="error-text">
                  <p
                    v-for="(rule, index) in dueDateAssociationsRules"
                    :key="index"
                    style="margin: 0;"
                  >
                    {{ rule }}
                  </p>
                </span>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-actions class="orangehrm-form-action">
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button
            :disabled="!canSubmit"
            display-type="secondary"
            :label="$t('general.save')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
    <TrainingDialog ref="trainingDialog" :departments="props.departments" />
    <InformationDialog
      ref="informationDialog"
      :departments="props.departments"
    />
    <TaskDialog ref="taskDialog" />
  </div>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-20 {
  width: 20%;
}

.flex {
  display: flex;
}

.gap-3 {
  gap: 3px;
}

.items-center {
  align-items: center;
}

.error-message {
  background-color: #fff1e6;
  border: 1px solid #ffdcc2;
  padding: 12px 30px;
  margin-bottom: 40px;
}

.error-text {
  color: #b45309;
  font-family: Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: bold;
  line-height: 20px;
  text-shadow: 1px 1px rgba(250, 250, 250, 0.3);
}
</style>
